import { Box, FieldSet, Gleam, Label, LabeledControl, Legend, Radio, VisuallyHidden } from '@zillow/constellation-10';
import { useContext } from 'react';
import { useQueryClient } from '@zg-rentals/react-query5';
import { SatelliteWebLandlordConversationHandler_getLatestConversationsForInbox } from '@zg-rentals/ts-satellite-client';
import { logError } from '@zg-rentals/log-error';
import { SatelliteConversationReadRepliedStatusTypeV2 } from '@zg-rentals/ts-satellite-client';
import useInboxRouterHelper from '../../../../hooks/useInboxRouterHelper';
import { InboxPageContext } from '../../../../contexts/InboxPageContextProvider';
import { useSatelliteWebLandlordConversationHandler_getCachedConversationCounts } from '@zg-rentals/ts-satellite-client-react-query';
import { buildRequestParams, getInboxConversationsQueryKey } from '../../../../hooks/useInboxConversationsQuery';

export default function StatusSelector() {
  const queryClient = useQueryClient();
  const { changeFiltersOrFolders } = useInboxRouterHelper();
  const { clientTimeZone, folder, listingAlias, isByProperty, readRepliedStatus, renterProgressStatus } =
    useContext(InboxPageContext);

  const { data } = useSatelliteWebLandlordConversationHandler_getCachedConversationCounts({
    meta: {
      tags: ['satellite:conversationCounts'],
      context: {
        api: 'getCachedConversationCounts',
        details: 'StatusSelector - Error getting unread counts',
      },
    },
  });

  const unreadConversationCount = data?.unreadCount || 0;
  const shouldShowUnreadCount = !isByProperty && unreadConversationCount > 0;

  const handleStatusChange = async (selectedStatus: SatelliteConversationReadRepliedStatusTypeV2) => {
    // Seed the exact query key the inbox will read after navigation, so the optimistic write isn't
    // wasted on a key nobody reads. Two alignments matter: (1) no daysBack — selecting a status
    // resets any widened lookback (see changeFiltersOrFolders); (2) listingAlias only when scoped
    // by property — useInboxConversationsQuery nulls it out when !isByProperty, so an unscoped view
    // with a conversation selected must not key on that conversation's listingAlias.
    const params = buildRequestParams({
      listingAlias: isByProperty ? listingAlias || null : null,
      clientTimeZone,
      folder,
      renterProgressStatus,
      readRepliedStatus: selectedStatus,
    });

    try {
      const result = await SatelliteWebLandlordConversationHandler_getLatestConversationsForInbox(params);
      queryClient.setQueryData(getInboxConversationsQueryKey(params), {
        pages: [result],
        pageParams: [undefined],
      });
    } catch (error) {
      logError({
        error,
        context: {
          details: 'StatusSelector - Error prefetching conversations for status change',
        },
      });
    }

    await changeFiltersOrFolders({
      folder,
      readRepliedStatus: selectedStatus,
      renterProgressStatus,
      byProperty: isByProperty,
      listingAlias: listingAlias,
    });
  };

  const getOnChangeHandler = (selectedStatus: SatelliteConversationReadRepliedStatusTypeV2) => () =>
    handleStatusChange(selectedStatus);

  return (
    <FieldSet
      legend={
        <VisuallyHidden>
          <Legend>Filter by conversation status</Legend>
        </VisuallyHidden>
      }
      css={{
        paddingLeft: 'tighter',
        paddingBottom: 'default',
      }}
    >
      <Box css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <LabeledControl
          label={<Label>Show unread only</Label>}
          control={
            <Radio
              name="status-filter-option"
              checked={readRepliedStatus === SatelliteConversationReadRepliedStatusTypeV2.UNREAD}
              onChange={getOnChangeHandler(SatelliteConversationReadRepliedStatusTypeV2.UNREAD)}
            />
          }
        />
        {shouldShowUnreadCount && (
          <Box>
            <Gleam appearance="ellipsis" count={unreadConversationCount} maxCount={99} tone="default" />
          </Box>
        )}
      </Box>

      <LabeledControl
        label={<Label>Not replied to</Label>}
        control={
          <Radio
            name="status-filter-option"
            checked={readRepliedStatus === SatelliteConversationReadRepliedStatusTypeV2.NOT_REPLIED_TO}
            onChange={getOnChangeHandler(SatelliteConversationReadRepliedStatusTypeV2.NOT_REPLIED_TO)}
          />
        }
      />
    </FieldSet>
  );
}
